import React, { useState, useEffect } from "react";
import { TextField, MenuItem, Button, Typography, Snackbar, Grid, CircularProgress, Card, CardContent, Box } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { ArrowBack, Save } from "@material-ui/icons";
import { API_POST } from "../../services/api.service";
import { getTeamDetails } from "../common/helper";
import { createHistoryEntry, TICKET_HISTORY_CONFIG } from "./ticketHistoryConfig";

const CreateTicketPage = ({ onBack, onSuccess, user, configData, teamMembers }) => {
  const [formData, setFormData] = useState({
    admin: user.email || '',
    username: user.firstName || user.username || '',
    ticketStatus: "Pending (By Default)",
    priority: "",
    developmentStatus: "",
    assignTo: "",
    asanaLink: "",
    estimationTime: "",
    deployedDate: "",
    description: "",
    comment: "",
    type: "",
    module: "",
    category: "",
    subCategory: ""
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
  const [fields, setFields] = useState([]);

  useEffect(() => {
    generateFields();
  }, [configData, teamMembers]);

  const generateFields = () => {
    const baseFields = [
     
      {
        key: "priority",
        label: "Priority",
        type: "select",
        options: [
          "Minus P1",
          "P0",
          "P1",
          "P2",
          "P3"
        ]
      },
      {
        key: "developmentStatus",
        label: "Development Status",
        type: "select",
        options: [
          "In Progress",
          "In Roadmap",
          "Under Development",
          "QA Tested",
          "Move For Deployment",
          "Not Found",
          "Under QA Testing",
          "Resolved",
          "Reopen"
        ]
      },
      { key: "assignTo", label: "Assign To", type: "select", options: teamMembers },
      { key: "asanaLink", label: "Asana Link", type: "text" },
      { key: "estimationTime", label: "Estimation Time", type: "text" },
      { key: "deployedDate", label: "Deployed Date", type: "date" },
      { key: "description", label: "Description", type: "textarea" },
    
    ];

    // Add dynamic fields based on configuration
    const dynamicFields = [...baseFields];

    // Add type field if configured
    if (configData.types && configData.types.length > 0) {
      dynamicFields.splice(1, 0, {
        key: "type",
        label: "Type",
        type: "select",
        options: configData.types
      });
    }

    // Add module field if configured
    if (configData.modules && configData.modules.length > 0) {
      dynamicFields.splice(2, 0, {
        key: "module",
        label: "Module",
        type: "select",
        options: configData.modules
      });
    }

    // Add category field if configured
    if (configData.categories && configData.categories.length > 0) {
      dynamicFields.splice(3, 0, {
        key: "category",
        label: "Category",
        type: "select",
        options: configData.categories
      });
    }

    // Add subcategory field if configured
    if (configData.subcategories && configData.subcategories.length > 0) {
      dynamicFields.splice(4, 0, {
        key: "subCategory",
        label: "SubCategory",
        type: "select",
        options: configData.subcategories
      });
    }

    setFields(dynamicFields);
  };

  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const currentTime = new Date().toISOString();
      
      // Create initial history entry for ticket creation
      const initialHistory = [
        createHistoryEntry('created', 'ticket', null, 'New ticket created', user)
      ];

      // Add history entries for each field that has a value
      TICKET_HISTORY_CONFIG.trackedFields.forEach(fieldName => {
        if (formData[fieldName] && formData[fieldName] !== '') {
          const historyEntry = createHistoryEntry(
            TICKET_HISTORY_CONFIG.fieldChangeTypes[fieldName] || 'updated',
            fieldName,
            null,
            formData[fieldName],
            user
          );
          initialHistory.push(historyEntry);
        }
      });

      const newData = {
        ...formData,
        admin: user.email || '',
        username: user.firstName || user.username || '',
        createdAt: currentTime,
        updatedAt: currentTime,
      //  history: initialHistory
      };

      await API_POST("action/addRecord", {
        root: "supportDevTickets",
        body: newData,
      });

      setSnackbar({ open: true, message: 'Ticket created successfully', color: 'success' });
      
      // Reset form
      setFormData({
        admin: user.email || '',
        username: user.firstName || user.username || '',
        ticketStatus: "Pending (By Default)",
        priority: "",
        developmentStatus: "",
        assignTo: "",
        asanaLink: "",
        estimationTime: "",
        deployedDate: "",
        description: "",
        comment: "",
        type: "",
        module: "",
        category: "",
        subCategory: ""
      });

      // Call success callback after a short delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess();
        }
      }, 1500);

    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error creating ticket', color: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field) => {
    if (field.key === "subCategory") {
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <Autocomplete
            options={field.options || []}
            value={formData.subCategory || null}
            onChange={(event, newValue) => handleFieldChange("subCategory", newValue || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                required={field.required}
              />
            )}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
          />
        </Grid>
      );
    }
    if (field.key === "category") {
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <Autocomplete
            options={field.options || []}
            value={formData.category || null}
            onChange={(event, newValue) => handleFieldChange("category", newValue || "")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field.label}
                variant="outlined"
                required={field.required}
              />
            )}
            freeSolo
            autoComplete
            includeInputInList
            filterSelectedOptions
          />
        </Grid>
      );
    }
    if (field.type === "select") {
      return (
        <Grid item xs={12} sm={6} md={4} key={field.key}>
          <TextField
            select
            label={field.label}
            value={formData[field.key] || ""}
            onChange={e => handleFieldChange(field.key, e.target.value)}
            fullWidth
            required={field.required}
            variant="outlined"
          >
            {field.options.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }
    if (field.key === "description" || field.key === "comment") {
      return (
        <Grid item xs={12} key={field.key}>
          <TextField
            label={field.label}
            value={formData[field.key] || ""}
            onChange={e => handleFieldChange(field.key, e.target.value)}
            fullWidth
            required={field.required}
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      );
    }
    return (
      <Grid item xs={12} sm={6} md={4} key={field.key}>
        <TextField
          label={field.label}
          type={field.type}
          value={formData[field.key] || ""}
          onChange={e => handleFieldChange(field.key, e.target.value)}
          fullWidth
          required={field.required}
          variant="outlined"
        />
      </Grid>
    );
  };

  return (
    <div style={{ margin: '0 auto', padding: 24 }}>
      <Box display="flex" alignItems="center" marginBottom={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onBack}
          style={{ marginRight: 16 }}
        >
          Back
        </Button>
        <Typography variant="h4" style={{ fontWeight: 600, color: '#1976d2' }}>
          Create New Support Dev Ticket
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Card style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(33, 150, 243, 0.07)' }}>
          <CardContent>
            <Grid container spacing={3}>
              {fields.map(field => renderField(field))}
            </Grid>
            <div style={{ marginTop: 32, textAlign: 'center' }}>
              <Button 
                type="submit" 
                color="primary" 
                variant="contained" 
                disabled={loading} 
                startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                style={{ minWidth: 140, fontWeight: 500 }}
              >
                {loading ? "Creating..." : "Create Ticket"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        ContentProps={{ style: { backgroundColor: snackbar.color === 'success' ? '#43a047' : '#d32f2f', color: '#fff' } }}
      />
    </div>
  );
};

export default CreateTicketPage; 